import { useEffect, useState } from 'react';
import { endpoints, type MealSlot } from '../api/endpoints';
import { ApiException } from '../api/client';
import { track } from '../lib/analytics';

const DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const SLOTS: { slot: MealSlot; label: string }[] = [
  { slot: 'breakfast', label: 'Завтрак' },
  { slot: 'lunch_soup', label: 'Обед · первое' },
  { slot: 'lunch_main', label: 'Обед · второе' },
  { slot: 'lunch_salad', label: 'Обед · салат' },
  { slot: 'dinner_main', label: 'Ужин · второе' },
  { slot: 'dinner_salad', label: 'Ужин · салат' },
];

export function AddToMenuModal({
  recipeId,
  recipeTitle,
  onAdded,
  onClose,
}: {
  recipeId: string;
  recipeTitle?: string;
  onAdded: () => void;
  onClose: () => void;
}) {
  const [dayIndex, setDayIndex] = useState<number | null>(null);
  const [slot, setSlot] = useState<MealSlot | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Escape закрывает (как SlotPickerModal/CookedModal).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function add() {
    if (dayIndex === null || slot === null) return;
    setBusy(true);
    setError(null);
    try {
      await endpoints.setMealEntry(dayIndex, slot, recipeId);
      track('favorite_to_menu', { slot });
      onAdded();
      onClose();
    } catch (e) {
      setError(e instanceof ApiException ? e.body.message : 'Не удалось добавить в меню');
    } finally {
      setBusy(false);
    }
  }

  const chip = (active: boolean) =>
    `rounded-full px-3 py-1.5 text-sm font-medium ${active ? 'bg-primary-600 text-white' : 'bg-white text-primary-700 shadow-sm'}`;

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="add-to-menu-title" className="fixed inset-0 z-30 flex flex-col bg-cream">
      <div className="flex items-center justify-between border-b border-black/5 px-4 py-3">
        <h2 id="add-to-menu-title" className="text-lg font-bold text-ink">В меню недели</h2>
        <button onClick={onClose} aria-label="Закрыть" className="text-ink/60">✕</button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {recipeTitle && <p className="mb-4 text-sm text-ink/60">{recipeTitle}</p>}
        <p className="mb-2 text-sm font-medium text-ink/70">День</p>
        <div className="mb-5 flex flex-wrap gap-2">
          {DAYS.map((label, i) => (
            <button key={label} type="button" onClick={() => setDayIndex(i)} aria-pressed={dayIndex === i} className={chip(dayIndex === i)}>
              {label}
            </button>
          ))}
        </div>
        <p className="mb-2 text-sm font-medium text-ink/70">Приём пищи</p>
        <div className="flex flex-wrap gap-2">
          {SLOTS.map(({ slot: s, label }) => (
            <button key={s} type="button" onClick={() => setSlot(s)} aria-pressed={slot === s} className={chip(slot === s)}>
              {label}
            </button>
          ))}
        </div>
        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      </div>
      <div className="border-t border-black/5 px-4 py-3">
        <button
          onClick={() => void add()}
          disabled={dayIndex === null || slot === null || busy}
          className="w-full rounded-xl bg-primary-600 px-4 py-3 font-semibold text-white disabled:opacity-50 active:scale-[0.98]"
        >
          {busy ? 'Добавляю…' : 'Добавить'}
        </button>
      </div>
    </div>
  );
}
